import { Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { NgForm, NgModel, FormsModule } from '@angular/forms';
import {
  AdressInputComponentTexte,
  ADRESSVORSCHLAG_FAILED_REASON,
  VermietetesBebautesModalObjekt,
  VermietetesBebautesModalObjektChangedPayload,
  VermietetesModalObjekt,
  VermietetesModalObjektChangedPayload,
  VermietetesObjekt,
  VermietetesObjektEnum,
  VermietetesUnbebautesModalObjektChangedPayload,
} from '@ps20-frontend/frontend-models';
import {
  TextPipe,
  TextService,
  HtmlPipe,
  ScreenreaderPipe,
  SimpleTextPipe,
} from '@ps20-frontend/text-service';
import { select } from '@angular-redux3/store';
import { Observable } from 'rxjs';
import { selectIsAzamUnderwriter } from '@ps20-frontend/selector';
import {
  CALLER_ID,
  GlobalActions,
  GlobalEventDispatcher,
} from '@ps20-frontend/shop-integration-redux';
import {
  createAriaLabelForObjektWithIndex,
  createAriaLabelForObjektWithIndexAndDescription,
  createAriaLabelForObjektWithIndexAndDescriptionAndError,
} from '@ps20-frontend/utils';
import { NxModalContentDirective, NxModalActionsDirective } from '@allianz/ng-aquila/modal';
import { NgTemplateOutlet, NgClass, AsyncPipe } from '@angular/common';
import { NxCardComponent } from '@allianz/ng-aquila/card';
import { NxtFieldSetModule } from '@nxt/ngx-ndbx-extensions/fieldset';
import { NxLinkComponent } from '@allianz/ng-aquila/link';
import { NxIconComponent } from '@allianz/ng-aquila/icon';
import { NxRadioGroupComponent, NxRadioComponent } from '@allianz/ng-aquila/radio-button';
import { NxErrorComponent } from '@allianz/ng-aquila/base';
import {
  NxFormfieldErrorDirective,
  NxFormfieldComponent,
  NxFormfieldSuffixDirective,
} from '@allianz/ng-aquila/formfield';
import { NxRowComponent, NxColComponent } from '@allianz/ng-aquila/grid';
import { NxInputDirective } from '@allianz/ng-aquila/input';
import { NxMaskDirective } from '@allianz/ng-aquila/mask';
import { NxNumberStepperComponent } from '@allianz/ng-aquila/number-stepper';
import { NgxCurrencyDirective } from 'ngx-currency';
import { NxMessageComponent } from '@allianz/ng-aquila/message';
import { NxButtonComponent } from '@allianz/ng-aquila/button';
import { RegexpPipe } from '@ps20-frontend/regexp-service';
import { MinNumericValueDirective } from '../min-numeric-value/min-numeric-value.directive';
import { MaxNumericValueDirective } from '../max-numeric-value/max-numeric-value.directive';
import { HinweisComponent } from '../hinweis-component/hinweis.component';
import { AdressInputComponent } from '../adresse/adress-input.component';
import { AuslaendischeAdresseCheckboxComponent } from '../auslaendische-adresse-checkbox/auslaendische-adresse-checkbox.component';
import { InfoIconComponent } from '../info-icon/info-icon.component';
import { VermieterInputProperties } from './vermieter-input.interface';

@Component({
  selector: 'ps20-frontend-vermieter-input',
  templateUrl: './vermieter-input.component.html',
  styleUrls: ['./vermieter-input.component.css'],
  imports: [
    FormsModule,
    NxModalContentDirective,
    NgTemplateOutlet,
    NgClass,
    InfoIconComponent,
    NxCardComponent,
    NxtFieldSetModule,
    NxLinkComponent,
    NxIconComponent,
    NxRadioGroupComponent,
    NxRadioComponent,
    NxErrorComponent,
    NxFormfieldErrorDirective,
    AuslaendischeAdresseCheckboxComponent,
    NxRowComponent,
    NxColComponent,
    NxFormfieldComponent,
    NxInputDirective,
    NxMaskDirective,
    AdressInputComponent,
    NxNumberStepperComponent,
    NgxCurrencyDirective,
    MinNumericValueDirective,
    MaxNumericValueDirective,
    HinweisComponent,
    NxMessageComponent,
    NxFormfieldSuffixDirective,
    NxModalActionsDirective,
    NxButtonComponent,
    AsyncPipe,
    TextPipe,
    HtmlPipe,
    ScreenreaderPipe,
    SimpleTextPipe,
    RegexpPipe,
  ],
})
export class VermieterInputComponent implements OnInit {
  private readonly textService = inject(TextService);
  private readonly globalEventDispatcher = inject<GlobalEventDispatcher>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- raw string DI token (globalEventDispatcherRef), not a typed InjectionToken
    'globalEventDispatcherRef' as any,
  );

  @select(selectIsAzamUnderwriter) isAzamUnderwriter$: Observable<boolean>;
  @ViewChild('form') form: NgForm;
  KEINE_AUSWAHL = 'Keine Auswahl';
  DEFAULT_MAX_VERMIETETE_OBJEKTE_ITEM_COUNT = 30;
  vermieterModalProperties: VermieterInputProperties;
  DEFAULT_MAX_STEPPER_VALUE = 30;
  EMPTY_STRING = '';

  @Input() vermieteteObjekte: VermietetesModalObjekt[] = [];
  @Input() vermieterModalPropertiesInput: VermieterInputProperties;
  @Input() callerId: CALLER_ID;

  @Output() vermietetesObjektChanged = new EventEmitter<VermietetesModalObjektChangedPayload>();
  @Output() vermietetesBebautObjektChanged =
    new EventEmitter<VermietetesBebautesModalObjektChangedPayload>();
  @Output() vermietetesUnbebautObjektChanged =
    new EventEmitter<VermietetesUnbebautesModalObjektChangedPayload>();
  @Output() vermietetesObjektAdded = new EventEmitter();
  @Output() vermietetesObjektDeleted = new EventEmitter<number>();
  @Output() confirmed = new EventEmitter<VermietetesObjekt[]>();
  @Output() auslaendischeAdresseClicked = new EventEmitter<boolean>();

  vermietetesObjektBebautEnum = VermietetesObjektEnum;
  orteTexte: AdressInputComponentTexte;
  strassenTexte: AdressInputComponentTexte;
  protected readonly createAriaLabelForObjektWithIndexAndDescriptionAndError =
    createAriaLabelForObjektWithIndexAndDescriptionAndError;
  protected readonly createAriaLabelForObjektWithIndex = createAriaLabelForObjektWithIndex;
  protected readonly createAriaLabelForObjektWithIndexAndDescription =
    createAriaLabelForObjektWithIndexAndDescription;

  constructor() {
    this.orteTexte = this.getOrtTexte();
    this.strassenTexte = this.getStrassenTexte();
  }

  ngOnInit(): void {
    this.vermieterModalProperties = {} as VermieterInputProperties;
    this.vermieterModalProperties.maxStepperValue =
      this.vermieterModalPropertiesInput?.maxStepperValue || this.DEFAULT_MAX_STEPPER_VALUE;
    this.vermieterModalProperties.maxVermieteteObjekteItemCount =
      this.vermieterModalPropertiesInput?.maxVermieteteObjekteItemCount ||
      this.DEFAULT_MAX_VERMIETETE_OBJEKTE_ITEM_COUNT;
    this.vermieterModalProperties.showStepperHint =
      this.vermieterModalPropertiesInput?.showStepperHint || false;
    this.vermieterModalProperties.isModal =
      this.vermieterModalPropertiesInput?.isModal !== undefined
        ? this.vermieterModalPropertiesInput.isModal
        : true;
    this.vermieteteObjekte.forEach((vermietetesObjekt, index) => {
      if (vermietetesObjekt.plz) {
        this.getAdressvorschlaege({ index: index, plz: vermietetesObjekt.plz });
      }
    });
  }

  plzChanged(index: number, plz: string, model: NgModel): void {
    this.vermietetesObjektChanged.emit({
      index,
      changes: { plz },
    });
    if (model.valid) {
      this.getAdressvorschlaege({ index, plz });
    }
  }

  ortChanged(index: number, ort: string): void {
    this.vermietetesObjektChanged.emit({
      index,
      changes: { ort },
    });
  }

  strasseChanged(index: number, strasse: string): void {
    this.vermietetesObjektChanged.emit({
      index,
      changes: { strasse },
    });
  }

  hausnummerChanged(index: number, hausnummer: string): void {
    this.vermietetesObjektChanged.emit({
      index,
      changes: { hausnummer },
    });
  }

  flurstuecknummerChanged(index: number, flurstueckNummer: string): void {
    this.vermietetesUnbebautObjektChanged.emit({
      index,
      changes: { flurstueckNummer },
    });
  }

  grundstueckBebautChanged(index: number, grundstueckBebaut: VermietetesObjektEnum): void {
    if (grundstueckBebaut === VermietetesObjektEnum.BEBAUT) {
      this.vermietetesBebautObjektChanged.emit({
        index,
        changes: { grundstueckBebaut },
      });
    }

    if (grundstueckBebaut === VermietetesObjektEnum.UNBEBAUT) {
      this.vermietetesUnbebautObjektChanged.emit({
        index,
        changes: { grundstueckBebaut },
      });
    }
  }

  anzahlWohneinheitChanged(index: number, wohneinheiten: number, model: NgModel): void {
    this.vermietetesBebautObjektChanged.emit({
      index,
      changes: {
        wohneinheiten: this.getAdjustedNumberStepperValue(wohneinheiten, model),
      },
    });
  }

  trackByFn(index: number): number {
    return index;
  }

  bruttojahresmieteWohneinheitChanged(index: number, bruttojahresmieteWohneinheit: Event): void {
    this.vermietetesBebautObjektChanged.emit({
      index,
      changes: {
        bruttojahresmieteWohneinheiten: +bruttojahresmieteWohneinheit,
      },
    });
  }

  anzahlGewerbeeinheitenChanged(index: number, gewerbeeinheiten: number, model: NgModel): void {
    this.vermietetesBebautObjektChanged.emit({
      index,
      changes: {
        gewerbeeinheiten: this.getAdjustedNumberStepperValue(gewerbeeinheiten, model),
      },
    });
  }

  bruttojahresmieteGewerbeeinheitenChanged(
    index: number,
    bruttojahresmieteGewerbeeinheiten: Event,
  ): void {
    this.vermietetesBebautObjektChanged.emit({
      index,
      changes: {
        bruttojahresmieteGewerbeeinheiten: +bruttojahresmieteGewerbeeinheiten,
      },
    });
  }

  anzahlStellplaetzeChanged(index: number, stellplaetze: number, model: NgModel): void {
    this.vermietetesBebautObjektChanged.emit({
      index,
      changes: {
        stellplaetze: this.getAdjustedNumberStepperValue(stellplaetze, model),
      },
    });
  }

  bruttojahresmieteStellplaetzeChanged(index: number, bruttojahresmieteStellplaetze: Event): void {
    this.vermietetesBebautObjektChanged.emit({
      index,
      changes: {
        bruttojahresmieteStellplaetze: +bruttojahresmieteStellplaetze,
      },
    });
  }

  jahresmieteKeyPress(event: KeyboardEvent): void {
    const pattern = /\d/;
    const inputChar = event.key;

    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  jahresmietePaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  grundstueckFlaecheChanged(index: number, grundstueckFlaeche: number): void {
    this.vermietetesUnbebautObjektChanged.emit({
      index,
      changes: { grundstueckFlaeche },
    });
  }

  addVermietetesObjekt(): void {
    this.vermietetesObjektAdded.emit();
  }

  deleteVermietetesObjekt(index: number): void {
    this.vermietetesObjektDeleted.emit(index);
  }

  onSubmit(form: NgForm): void {
    if (
      form.valid &&
      this.vermieteteObjekte.every(value =>
        value.grundstueckBebaut === VermietetesObjektEnum.BEBAUT
          ? this.isBebauteGrundstueckeGesetzt(value)
          : true,
      )
    ) {
      this.confirmed.emit();
    }
  }

  canAddVermietetesObjektItem(): boolean {
    return (
      this.vermieteteObjekte.length < this.vermieterModalProperties.maxVermieteteObjekteItemCount
    );
  }

  shouldShowEntfernenLink(): boolean {
    return this.vermieteteObjekte.length > 1;
  }

  isBebauteGrundstueckeGesetzt(vermietetesObjekt: VermietetesBebautesModalObjekt): boolean {
    return (
      vermietetesObjekt.wohneinheiten +
        vermietetesObjekt.gewerbeeinheiten +
        vermietetesObjekt.stellplaetze !==
      0
    );
  }

  getOrtTexte(): AdressInputComponentTexte {
    const textPipe = new TextPipe(this.textService);
    return {
      label: textPipe.transform('vermieter.adresse.ort.label'),
      required: textPipe.transform('vermieter.adresse.ort.required'),
      pattern: textPipe.transform('vermieter.adresse.ort.pattern'),
      dropdownPlaceholder: textPipe.transform('vermieter.adresse.ort.dropdownPlaceholder'),
    };
  }

  getStrassenTexte(): AdressInputComponentTexte {
    const textPipe = new TextPipe(this.textService);
    return {
      label: textPipe.transform('vermieter.adresse.strasse.label'),
      required: textPipe.transform('vermieter.adresse.strasse.required'),
      pattern: textPipe.transform('vermieter.adresse.strasse.pattern'),
      dropdownPlaceholder: textPipe.transform('vermieter.adresse.strasse.dropdownPlaceholder'),
    };
  }

  getAuslaendischeAdresseText(): string {
    this.textService.setContentKey('AZAM');
    return this.textService.get('auslaendische.adresse');
  }

  getSubmitButtonDisabled(): boolean {
    return !this.vermieteteObjekte.every(
      value =>
        (!!value.ort && !!value.auslaendischeAdresse === false) ||
        (!!value.ort === false && !!value.auslaendischeAdresse),
    );
  }

  shouldShowBebauetesGruendstueckRequiredError(
    bebautesGrundstueck: NgModel,
    form: NgForm,
  ): boolean {
    return !!((bebautesGrundstueck.touched || form.submitted) && bebautesGrundstueck.invalid);
  }

  shouldShowPlzPatternError(
    vermietetesObjekt: VermietetesModalObjekt,
    postleitzahl?: NgModel,
  ): boolean {
    return !!(
      vermietetesObjekt.lastError === ADRESSVORSCHLAG_FAILED_REASON.PLZ_INVALID &&
      !postleitzahl?.errors
    );
  }

  shouldShowObiektAnzahlRequiredError(
    vermietetesObjekt: VermietetesBebautesModalObjekt,
    wohneinheitenStepper: NgModel,
    gewerbeeinheitStepper: NgModel,
    stellplaetzeStepper: NgModel,
    form: NgForm,
  ): boolean {
    return !!(
      !this.isBebauteGrundstueckeGesetzt(vermietetesObjekt) &&
      (wohneinheitenStepper.touched ||
        gewerbeeinheitStepper.touched ||
        stellplaetzeStepper.touched ||
        form.submitted)
    );
  }

  private getAdjustedNumberStepperValue(numberStepperValue: number, model: NgModel): number {
    const adjustedValue =
      numberStepperValue > this.vermieterModalProperties.maxStepperValue
        ? this.vermieterModalProperties.maxStepperValue
        : numberStepperValue;
    model.control.setValue(adjustedValue);
    return adjustedValue;
  }

  auslaendischeAdressChecboxChanged(
    index: number,
    auslaendischeAdresse: boolean,
    grundstueckBebaut: VermietetesObjektEnum,
  ): void {
    this.vermietetesObjektChanged.emit({
      index,
      changes: { auslaendischeAdresse },
    });
    if (auslaendischeAdresse) {
      this.clearEnteredAddressData(index, grundstueckBebaut);
    }
  }

  private clearEnteredAddressData(index: number, grundstueckBebaut: VermietetesObjektEnum): void {
    const plz = '';
    this.vermietetesObjektChanged.emit({ index, changes: { plz } });
    this.strasseChanged(index, this.EMPTY_STRING);
    this.ortChanged(index, this.EMPTY_STRING);
    if (grundstueckBebaut === VermietetesObjektEnum.UNBEBAUT) {
      this.flurstuecknummerChanged(index, '');
    } else {
      this.hausnummerChanged(index, this.EMPTY_STRING);
    }
  }

  shouldShowStepperError(i: number): boolean {
    return i === this.vermieterModalProperties.maxStepperValue;
  }

  protected readonly JSON = JSON;

  addKeineAuswahlOption(strassen: Array<string>, bebaut: number): Array<string> {
    if (bebaut == 2) {
      return [this.KEINE_AUSWAHL].concat(strassen);
    }
    return strassen;
  }

  getAdressvorschlaege(plzChanged: { index: number; plz: string }): void {
    this.globalEventDispatcher.dispatch(
      new GlobalActions().getAdressvorschlag({
        callerId: this.callerId,
        callerData: { index: plzChanged.index, nurOrte: false },
        plz: plzChanged.plz,
      }),
    );
  }

  addAriaLabelToAdressInputComponent(
    texte: AdressInputComponentTexte,
    objectLabel: string,
    index: number,
  ): AdressInputComponentTexte {
    return {
      ...texte,
      ariaLabel: this.createAriaLabelForObjektWithIndex(objectLabel, index),
    };
  }
}
